import Typography from '@prezzee/ribbon-ui/lib/components/Typography';
import {
  AlertStatus,
  Alert,
  AlertIcon,
  AlertTextContainer,
  AlertTitle,
  AlertDescription,
  AlertColor,
} from '@prezzee/alert';
import { VStack } from '@prezzee/ribbon-ui/lib/components/Stack';
import Box from '@prezzee/ribbon-ui/lib/components/Box';
import Button from '@prezzee/ribbon-ui/lib/components/Button';
import ReactMarkdown from 'react-markdown';
import { css } from '@emotion/css';
import { spacing, colors } from '@prezzee/tokens';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Link from 'next/link';
import { useProduct } from 'contexts/ProductPageContext';
import { IconTags } from 'models/Models';
import { Fragment } from 'react';
import useDisclosure from '@prezzee/ribbon-ui/lib/hooks/useDisclosure';
import CardDisplayImage from './CardDisplayImage';
import RetailerListModal from './RetailerListModal';
import ClampLines from 'components/common/ClampLines';
import IconTag from './IconTag';
import useWindowSize from 'utils/useWindowSize';
import ZagBuyingPrompt from './zag/ZagBuyingPrompt';
import { ZagBuyingPrompt as ZagBuyingPromptT } from 'api/queries/zagBuyingPrompt';
import { Nullable } from '../../../models/types';

const style = {
  backLink: css`
    margin-bottom: ${spacing.spacing06};

    svg {
      margin-right: ${spacing.spacing04};
      color: ${colors.purple400};
    }

    a:focus {
      outline: 5px auto Highlight;
      outline: 5px auto -webkit-focus-ring-color;
    }
  `,
  alertBanner: css`
    margin-bottom: ${spacing.spacing04} !important;
    box-shadow: none !important;

    a {
      color: ${colors.blue400};

      &:hover,
      &:focus {
        text-decoration: underline;
      }
    }
  `,
};

type CardSummaryProps = {
  iconTags: IconTags;
  zagBuyingPrompt: Nullable<ZagBuyingPromptT>;
};

type AlertProps = {
  color: AlertColor;
  status: AlertStatus;
};

type AlertMap = {
  critical: AlertProps;
  warning: AlertProps;
  success: AlertProps;
  info: AlertProps;
};

const AlertObject: AlertMap = {
  critical: {
    color: 'red',
    status: 'error',
  },
  warning: {
    color: 'yellow',
    status: 'warning',
  },
  success: {
    color: 'green',
    status: 'success',
  },
  info: {
    color: 'blue',
    status: 'info',
  },
};

const CardSummary = ({ iconTags, zagBuyingPrompt }: CardSummaryProps) => {
  const retailerListDialog = useDisclosure();
  const { sku, isLayer, configs } = useProduct();
  const { width } = useWindowSize();
  const skuTagTitle = iconTags?.skuTagTitle || '';
  const skuIconTags = iconTags?.skuIconTags?.items || [];

  return (
    <Box pt={6} w={{ base: '100%', lg: '50%' }}>
      <Box mr={{ lg: '70px' }} position={{ lg: 'sticky' }} top={{ lg: '164px' }}>
        <div className={style.backLink}>
          <Link href="/store">
            <a>
              <FontAwesomeIcon icon={['fas', 'chevron-left']} />
              Back
            </a>
          </Link>
        </div>
        <VStack spacing="4" mb="2" alignItems="flex-start">
          <CardDisplayImage />
          {configs.featureLayerBuyingWeb && width < 987 && sku?.layerEnabled && sku?.layerType === 'default' && (
            <ZagBuyingPrompt
              imageUrl={zagBuyingPrompt?.elements?.image?.value?.[0]?.url}
              title={zagBuyingPrompt?.elements?.title?.value}
              description={zagBuyingPrompt?.elements?.description?.value?.replace('{0}', sku.name)}
              modalLinkText={zagBuyingPrompt?.elements?.zag_explanation_modal_link?.value}
            />
          )}
          <Typography variant="h3" as="h1">
            {isLayer ? sku.layerDisplayName : sku.displayName}
          </Typography>
          {sku.alerts?.length > 0 &&
            sku.alerts.map((data, index) => {
              const { level, title, messageMarkdown } = data;
              const alertProps: AlertProps = AlertObject[level];

              return (
                <Alert
                  className={style.alertBanner}
                  fullWidth
                  boxShadow
                  status={alertProps.status}
                  color={alertProps.color}
                  key={index}
                >
                  <AlertIcon />
                  <AlertTextContainer>
                    <AlertTitle>{title}</AlertTitle>
                    <AlertDescription>
                      <ReactMarkdown>{messageMarkdown}</ReactMarkdown>
                    </AlertDescription>
                  </AlertTextContainer>
                </Alert>
              );
            })}

          {sku?.descriptionShort && <ClampLines lines={3} text={sku.descriptionShort} id="description-short" />}

          <Typography variant="body2" color="gray.600" as="h3">
            {sku.expiryTermDisplay}
          </Typography>

          {skuTagTitle && (
            <Typography variant="body1" as="h3">
              {skuTagTitle}
            </Typography>
          )}

          {skuIconTags?.length > 0 && (
            <Box display="flex" flexWrap="wrap">
              {skuIconTags.map((iconTag, index) => {
                const text = iconTag.text;
                const url = iconTag.icon?.items && iconTag.icon.items.length ? iconTag.icon.items[0].url : null;
                const boldText = !!(iconTag.boldText?.items && iconTag.boldText.items.length > 0);

                if (!url) return null;
                return <IconTag text={text} iconUrl={url} boldText={boldText} key={index} />;
              })}
            </Box>
          )}
        </VStack>

        {sku.isSwappable && (
          <Fragment>
            <Button variant="link" onClick={retailerListDialog.onOpen}>
              See all brands and retailers
            </Button>
            {retailerListDialog.isOpen && <RetailerListModal onClose={retailerListDialog.onClose} uid={sku.uid} />}
          </Fragment>
        )}

        {isLayer && retailerListDialog.isOpen && (
          <RetailerListModal onClose={retailerListDialog.onClose} title="Available brands" showDefaultStore />
        )}
      </Box>
    </Box>
  );
};

export default CardSummary;
